import React, { useEffect, useState } from 'react';
import Layout from '../../components/layout/AppLayout';
import { useSidebar } from '../../../shared/contexts/SidebarContext';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  getAvailability,
  acceptRejectLeave,
  getFollowUpsByProjectId,
  downloadInvoice
} from '../../services/api/implementation/impl';
import { useNavigate } from 'react-router-dom';
import Button from '../../../shared/components/ui/Button';
import Modal from '../../../shared/components/ui/Modal';
import toast from 'react-hot-toast';
import Cookies from 'js-cookie';
import mixpanel from 'mixpanel-browser';
import Loader from '../../../shared/components/ui/Loader';
import Header from '../../../shared/components/layout/Header';
import SalesGraphs from './SalesGraphs';
import DashboardFilters from '../../../shared/components/ui/DashboardFilters';

const SalesDashboard = ({
  skipLayout = false,
  skipHeader = false,
  skipFilters = false,
  projectID: externalProjectID = null,
  month: externalMonth = null,
  onFiltersChange: externalOnFiltersChange = null
}) => {
  const { collapsed } = useSidebar();
  const [selectedActivity, setSelectedActivity] = useState();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [filters, setFilters] = useState({
    projectID: externalProjectID || null,
    month: externalMonth || null
  });
  const [isFiltersLoading, setIsFiltersLoading] = useState(false);
  const navigate = useNavigate();
  const userID = Number(Cookies.get('userId'));
  const [designation, setDesignation] = useState('');
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState(null);

  useEffect(() => {
    setDesignation(Cookies.get('userDesignationLevel'));
  }, []);

  const { data: availability, refetch: refetAvailability } = useQuery({
    queryKey: ['allAvailability', filters.projectID],
    queryFn: () => getAvailability(filters.projectID),
    enabled: !!filters.projectID
  });

  const { data: followUps, refetch: refetchFollowUps } = useQuery({
    queryKey: ['followUps', filters.projectID],
    queryFn: () => getFollowUpsByProjectId(filters.projectID),
    enabled: !!filters.projectID,
    onSuccess: (data) => {
      mixpanel.track('Follow Ups API Success', {
        user_id: userID,
        project_id: filters.projectID,
        follow_ups_count: data?.data?.length || 0,
        timestamp: new Date().toISOString()
      });
    },
    onError: (error) => {
      mixpanel.track('Follow Ups API Failed', {
        user_id: userID,
        project_id: filters.projectID,
        error_message: error?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    }
  });

  const handleFiltersChange = ({ projectID, month }) => {
    setFilters({ projectID, month });
    // Also notify parent if external handler is provided
    if (externalOnFiltersChange) {
      externalOnFiltersChange({ projectID, month });
    }
  };

  const handleFiltersLoadingChange = (isLoading) => {
    setIsFiltersLoading(isLoading);
  };

  // Update filters when external props change
  useEffect(() => {
    if (externalProjectID !== null || externalMonth !== null) {
      setFilters({
        projectID: externalProjectID || filters.projectID,
        month: externalMonth || filters.month
      });
    }
  }, [externalProjectID, externalMonth]);

  const AvatarCircle = ({ name }) => (
    <div className="w-6 h-6 rounded-full bg-yellow-500 text-white flex items-center justify-center text-xs font-semibold border-2 border-white">
      {name?.charAt(0).toUpperCase()}
    </div>
  );

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short'
    });
  };

  // Helper function to group and sort data
  const processAvailabilityData = (data) => {
    if (!data) return [];

    // Sort by date
    const sorted = [...data].sort((a, b) => new Date(a.date) - new Date(b.date));

    // Group by formatted date
    const grouped = sorted.reduce((groups, item) => {
      const dateKey = formatDate(item.date);
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(item);
      return groups;
    }, {});

    return Object.entries(grouped);
  };

  // Pre-filter all pending approval items so only those with status not true are used
  const filteredAvailabilityData = availability
    ? processAvailabilityData(availability.data)
      .map(([date, items]) => [date, items.filter((item) => !item.status)])
      .filter(([, filteredItems]) => filteredItems.length > 0) // remove empty dates
    : [];

  const processFollowUpsData = (data) => {
    if (!data || !Array.isArray(data)) return [];

    let currentUserName = '';

    // First find current user's name from the data
    const currentUserItem = data.find((item) => item.user?.id === userID);
    if (currentUserItem) {
      currentUserName = currentUserItem.user.name;
    }

    // Categorize follow-ups
    const categorized = {
      myFollowUps: {
        byDate: {} // key will be date string (e.g., "24 Apr")
      },
      otherSales: {} // key will be user ID
    };

    // Helper function to format date as "DD MMM"
    const formatDate = (dateString) => {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
    };

    data.forEach((item) => {
      // Skip completed items
      if (item.status === 'Inactive') return;

      const isMine = item.user?.id === userID;

      if (isMine) {
        // For my follow-ups, group by date
        const followUpDate = item.nextFollowUpDate ? formatDate(item.nextFollowUpDate) : 'No Date';

        if (!categorized.myFollowUps.byDate[followUpDate]) {
          categorized.myFollowUps.byDate[followUpDate] = {
            default: [],
            payment: [],
            possession: [],
            closeddeal: [],
            underConsideration: [],
            reviewMeeting: [],
            reportReview: [],
            invoiceGenerated: []
          };
        }

        if (item.type === 'Default') {
          categorized.myFollowUps.byDate[followUpDate].default.push(item);
        } else if (item.type === 'Payment') {
          categorized.myFollowUps.byDate[followUpDate].payment.push(item);
        } else if (item.type === 'Posession Date') {
          categorized.myFollowUps.byDate[followUpDate].possession.push(item);
        } else if (item.type === 'ClosedDeal Details') {
          categorized.myFollowUps.byDate[followUpDate].closeddeal.push(item);
        } else if (item.type === 'UnderConsideration Details') {
          categorized.myFollowUps.byDate[followUpDate].underConsideration.push(item);
        } else if (item.type === 'Review Meeting') {
          categorized.myFollowUps.byDate[followUpDate].reviewMeeting.push(item);
        } else if (item.type === 'Report Review') {
          categorized.myFollowUps.byDate[followUpDate].reportReview.push(item);
        } else if (item.type === 'Invoice Generated') {
          categorized.myFollowUps.byDate[followUpDate].invoiceGenerated.push(item);
        }
      } else {
        // For other sales people (keep existing structure)
        const salesId = item.user?.id;
        if (!salesId) return;

        if (!categorized.otherSales[salesId]) {
          categorized.otherSales[salesId] = {
            user: item.user,
            escalated: [],
            absent: []
          };
        }

        if (item.isEscalated) {
          categorized.otherSales[salesId].escalated.push(item);
        }
        if (item.isAbsent) {
          categorized.otherSales[salesId].absent.push(item);
        }
      }
    });

    // Create display structure
    const displayData = [];

    // Add My Follow Ups section if there are any
    const myFollowUpDates = Object.keys(categorized.myFollowUps.byDate);
    if (myFollowUpDates.length > 0) {
      displayData.push({
        type: 'header',
        title: 'My Follow Up'
      });

      // Sort dates chronologically (convert to Date objects for proper sorting)
      const sortedDates = myFollowUpDates
        .filter((date) => date !== 'No Date')
        .sort((a, b) => {
          const dateA = new Date(a);
          const dateB = new Date(b);
          return dateA - dateB;
        });

      // Add "No Date" items at the end if they exist
      if (categorized.myFollowUps.byDate['No Date']) {
        sortedDates.push('No Date');
      }

      sortedDates.forEach((date) => {
        const dateData = categorized.myFollowUps.byDate[date];

        // Add date header
        displayData.push({
          type: 'dateHeader',
          date: date
        });

        // Add default follow-ups for this date
        if (dateData.default.length > 0) {
          displayData.push({
            type: 'item',
            category: 'default',
            user: { id: userID, name: currentUserName },
            count: dateData.default.length,
            items: dateData.default,
            date: date
          });
        }

        // Add payment follow-ups for this date
        if (dateData.payment.length > 0) {
          displayData.push({
            type: 'item',
            category: 'payment',
            user: { id: userID, name: currentUserName },
            count: dateData.payment.length,
            items: dateData.payment,
            date: date
          });
        }

        // Add possession date follow-ups for this date
        if (dateData.possession.length > 0) {
          displayData.push({
            type: 'item',
            category: 'possession',
            user: { id: userID, name: currentUserName },
            count: dateData.possession.length,
            items: dateData.possession,
            date: date
          });
        }

        // Add closed deal details follow-ups for this date
        if (dateData.closeddeal.length > 0) {
          displayData.push({
            type: 'item',
            category: 'closeddeal',
            user: { id: userID, name: currentUserName },
            count: dateData.closeddeal.length,
            items: dateData.closeddeal,
            date: date
          });
        }

        // Add under consideration details follow-ups for this date
        if (dateData.underConsideration.length > 0) {
          displayData.push({
            type: 'item',
            category: 'underConsideration',
            user: { id: userID, name: currentUserName },
            count: dateData.underConsideration.length,
            items: dateData.underConsideration,
            date: date
          });
        }

        // Add help request follow-ups for this date
        if (dateData.reviewMeeting.length > 0) {
          displayData.push({
            type: 'item',
            category: 'reviewMeeting',
            user: { id: userID, name: currentUserName },
            count: dateData.reviewMeeting.length,
            items: dateData.reviewMeeting,
            date: date
          });
        }

        // Add report review follow-ups for this date
        if (dateData.reportReview.length > 0) {
          displayData.push({
            type: 'item',
            category: 'reportReview',
            user: { id: userID, name: currentUserName },
            count: dateData.reportReview.length,
            items: dateData.reportReview,
            date: date
          });
        }

        // Add invoice generated follow-ups for this date
        if (dateData.invoiceGenerated.length > 0) {
          dateData.invoiceGenerated.forEach((invItem) => {
            displayData.push({
              type: 'item',
              category: 'invoiceGenerated',
              user: { id: userID, name: currentUserName },
              count: 1,
              items: [invItem],
              date: date
            });
          });
        }
      });
    }

    // Add Other Sales section if there are any (keep existing structure)
    const otherSalesEntries = Object.values(categorized.otherSales);
    if (otherSalesEntries.length > 0) {
      displayData.push({
        type: 'header',
        title: 'Other Sales Follow Ups'
      });

      otherSalesEntries.forEach((salesData) => {
        if (salesData.escalated.length > 0) {
          displayData.push({
            type: 'item',
            category: 'escalated',
            user: salesData.user,
            count: salesData.escalated.length,
            items: salesData.escalated
          });
        }

        if (salesData.absent.length > 0) {
          displayData.push({
            type: 'item',
            category: 'absent',
            user: salesData.user,
            count: salesData.absent.length,
            items: salesData.absent
          });
        }
      });
    }

    return displayData;
  };

  const handleAcceptLeave = () => {
    const payload = {
      status: true
    };

    // Track leave acceptance
    mixpanel.track('Leave Request Accepted', {
      user_id: userID,
      activity_id: selectedActivity,
      timestamp: new Date().toISOString()
    });

    handleAcceptRejectTarget({ payload, type: 'Accepted' });
  };

  const handleRejectLeave = () => {
    const payload = {
      status: false
    };

    // Track leave rejection
    mixpanel.track('Leave Request Rejected', {
      user_id: userID,
      activity_id: selectedActivity,
      timestamp: new Date().toISOString()
    });

    handleAcceptRejectTarget({ payload, type: 'Rejected' });
  };

  const { mutate: handleDownloadInvoice } = useMutation({
    mutationFn: async (dealId) => {
      setDownloadingInvoiceId(dealId);
      return await downloadInvoice(dealId);
    },
    onSuccess: (data, dealId) => {
      try {
        const blob = new Blob([data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `invoice_${dealId}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Invoice downloaded successfully');

        mixpanel.track('Invoice Downloaded from Dashboard', {
          user_id: userID,
          deal_id: dealId,
          timestamp: new Date().toISOString()
        });
      } catch (err) {
        console.error('Error creating download:', err);
        toast.error('Error downloading invoice');
      } finally {
        setDownloadingInvoiceId(null);
      }
    },
    onError: (err, dealId) => {
      console.error('Error downloading invoice:', err);
      toast.error(err?.response?.data?.message || err?.message || 'Error downloading invoice');
      setDownloadingInvoiceId(null);

      mixpanel.track('Invoice Download Failed', {
        user_id: userID,
        deal_id: dealId,
        error_message: err?.response?.data?.message || err?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    }
  });

  const { mutate: handleAcceptRejectTarget } = useMutation({
    mutationFn: ({ payload }) => acceptRejectLeave(payload, selectedActivity),
    onMutate: () => {
      setIsSubmitting(true); // Disable the button when mutation starts

      mixpanel.track('Leave Request Process Started', {
        user_id: userID,
        activity_id: selectedActivity,
        timestamp: new Date().toISOString()
      });
    },
    onSuccess: async (result) => {
      setIsSubmitting(false);

      // Check if result is an error object (API now returns errors instead of throwing)
      if (
        result?.response?.status ||
        result?.message?.includes('Error') ||
        result?.name === 'AxiosError'
      ) {
        // Handle as error
        console.error('Error accepting/rejecting leaves', result);
        toast.error(
          result?.response?.data?.message || result?.message || 'Error accepting/rejecting leaves'
        );

        mixpanel.track('Leave Request Process Failed', {
          user_id: userID,
          activity_id: selectedActivity,
          error_message: result?.response?.data?.message || result?.message || 'Unknown error',
          timestamp: new Date().toISOString()
        });
        return;
      }

      // Handle as success
      const type = result?.type || 'processed';
      toast.success(`Leave ${type} Successfully`);
      setShowConfirmationModal(false);

      mixpanel.track('Leave Request Process Success', {
        user_id: userID,
        activity_id: selectedActivity,
        action_type: type,
        timestamp: new Date().toISOString()
      });

      await refetAvailability();
      await refetchFollowUps();
    },
    onError: (err) => {
      setIsSubmitting(false);
      console.error('Error accepting/rejecting leaves', err);
      toast.error(
        err?.response?.data?.message || err?.message || 'Error accepting/rejecting leaves'
      );

      mixpanel.track('Leave Request Process Failed', {
        user_id: userID,
        activity_id: selectedActivity,
        error_message: err?.response?.data?.message || err?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    }
  });

  const content = (
    <>
      <Loader isVisible={isSubmitting || isFiltersLoading} />
      {showConfirmationModal && (
        <Modal
          isDirty
          confirmOnClose
          width={45}
          name="Confirmation"
          onClose={() => {
            // Track modal close
            mixpanel.track('Leave Confirmation Modal Closed', {
              user_id: userID,
              activity_id: selectedActivity,
              timestamp: new Date().toISOString()
            });

            setShowConfirmationModal(false);
            setSelectedActivity();
          }}>
          <div className="flex flex-col justify-start items-center z-10">
            <h1 className=" text-center text-[1.4rem] font-medium text-primary">
              Are you sure you want to Accept this leave?
            </h1>
            <div className="w-full flex justify-end items-center space-x-4 mt-8">
              <Button type="reset" onClick={() => handleRejectLeave()} displayType="secondary">
                Reject
              </Button>

              <Button
                disabled={isSubmitting}
                type="button"
                onClick={(e) => {
                  handleAcceptLeave();
                }}
                displayType="primary-red">
                Accept
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {!skipHeader && (
        <div
          className={`fixed top-0 z-10 transition-all duration-300 bg-white ${collapsed ? 'left-[76px] w-[calc(100%-76px)]' : 'left-64 w-[calc(100%-16rem)]'
            }`}>
          <Header data={{ name: 'Dashboard' }} />
          {!skipFilters && (
            <DashboardFilters
              onFiltersChange={handleFiltersChange}
              onLoadingChange={handleFiltersLoadingChange}
              top="4rem"
            />
          )}
        </div>
      )}

      {/* Body Content */}
      <div className={`transition-all duration-300 ease-in-out ${collapsed ? 'ml-[1vw]' : ''}`}>
        <div
          className={`flex justify-flex-start items-start relative p-2 gap-7 pl-5 ${skipHeader && skipFilters ? 'pt-[7rem]' : 'pt-[8.8rem]'
            }`}>
          {/* Graphs Section */}
          <div
            className={`flex flex-col gap-5 transition-all duration-300 ease-in-out ${collapsed ? 'w-[63%]' : 'w-[59%]'
              }`}>
            <SalesGraphs projectID={filters.projectID} month={filters.month} />
          </div>

          {/* Priorities and Approvals - Fixed */}
          <div
            className={`h-[75vh] overflow-hidden shadow flex flex-col bg-gray-100 w-[31%] fixed right-4 ${skipHeader && skipFilters ? 'top-[14rem]' : 'top-[10.4rem]'
              }`}>
            <div className="flex-1 overflow-y-auto rounded-lg bg-white p-4 shadow-lg">
              {/* Priorities Section */}
              {/* <div className="text-center font-medium text-sm mb-5 flex gap-3 justify-center items-center">
              <span className="text-[#346C75]">28th Apr</span> to
              <span className="text-[#346C75]">04th May</span>
            </div> */}
              <div className="mb-6 border border-gray-300 rounded-lg p-4">
                <h3 className="text-md font-semibold mb-4">Priorities</h3>
                {followUps && processFollowUpsData.length > 0 ? (
                  processFollowUpsData(followUps?.data).map((item, index) => {
                    if (item.type === 'header') {
                      return (
                        <p
                          key={`header-${index}`}
                          className="font-semibold text-sm text-gray-600 mb-2 mt-4">
                          {item.title}
                        </p>
                      );
                    }

                    if (item.type === 'dateHeader') {
                      return (
                        <p
                          key={`date-header-${index}`}
                          className="font-semibold text-sm text-gray-600 mb-2 mt-4">
                          {item.date}
                        </p>
                      );
                    }

                    let label = '';
                    let action = '';

                    switch (item.category) {
                      case 'default':
                        label = 'Follow Up';
                        action = 'Leads';
                        break;
                      case 'payment':
                        label = 'Payment Follow Up';
                        action = 'Leads';
                        break;
                      case 'possession':
                        label = 'Possession Date';
                        action = 'Leads';
                        break;
                      case 'closeddeal':
                        label = 'Closed Deal Details';
                        action = 'Leads';
                        break;
                      case 'underConsideration':
                        label = 'Under Consideration Details';
                        action = 'Leads';
                        break;
                      case 'reviewMeeting':
                        label = 'Attend sales review meeting';
                        action = 'Reports';
                        break;
                      case 'reportReview':
                        label = 'Report review for sales meeting';
                        action = 'Reports';
                        break;
                      case 'escalated':
                        label = 'Escalation';
                        action = 'Pending Tasks';
                        break;
                      case 'absent':
                        label = 'Is Absent';
                        action = 'Assign Agent';
                        break;
                      case 'invoiceGenerated':
                        label = 'Invoice Generated';
                        action = 'Leads';
                        break;
                    }

                    if (item.category === 'invoiceGenerated') {
                      const detail = item.items[0];
                      const dealId = detail?.invoice?.deal_id;
                      const isDownloading = downloadingInvoiceId === dealId;

                      return (
                        <div
                          key={`${item?.user?.id}-${item?.category}-${index}`}
                          className="flex flex-col gap-2 border border-gray-200 rounded-lg p-3 cursor-pointer hover:bg-gray-50 mb-2 transition-colors"
                          onClick={() => {
                            if (item.user?.id === Number(Cookies.get('userId'))) {
                              toast('invoiceGenerated');
                            }
                          }}>
                          <div className="flex justify-between items-start">
                            <div className="flex items-center gap-2">
                              <AvatarCircle name={item?.user?.name} />
                              <span className="text-sm">
                                <strong>{item?.user?.name}</strong> | {label}
                              </span>
                            </div>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (dealId && !isDownloading) {
                                  handleDownloadInvoice(dealId);
                                }
                              }}
                              disabled={isDownloading}
                              className="px-3 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors">
                              {isDownloading ? 'Downloading...' : 'Download'}
                            </button>
                          </div>
                          <div className="pl-8 text-xs text-gray-600 space-y-1">
                            <p>
                              Customer: <strong>{detail?.lead?.user?.name || 'N/A'}</strong>
                            </p>
                            <p>
                              Project: {detail?.project?.name || 'N/A'} | Amount: ₹
                              {detail?.invoice?.amount || 0}
                            </p>
                            {(detail?.unit?.block?.name ||
                              detail?.unit?.name ||
                              detail?.invoice?.closedDeal?.unit?.unitName ||
                              detail?.invoice?.closedDeal?.unit?.block?.name) && (
                                <p>
                                  {(detail?.unit?.block?.name ||
                                    detail?.invoice?.closedDeal?.unit?.block?.name) &&
                                    `Block: ${detail?.unit?.block?.name ||
                                    detail?.invoice?.closedDeal?.unit?.block?.name
                                    }`}
                                  {(detail?.unit?.block?.name ||
                                    detail?.invoice?.closedDeal?.unit?.block?.name) &&
                                    (detail?.unit?.name ||
                                      detail?.invoice?.closedDeal?.unit?.unitName) &&
                                    ' | '}
                                  {(detail?.unit?.name ||
                                    detail?.invoice?.closedDeal?.unit?.unitName) &&
                                    `Unit Name : ${detail?.unit?.name ||
                                    detail?.invoice?.closedDeal?.unit?.unitName
                                    }`}
                                </p>
                              )}
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div
                        key={`${item?.user?.id}-${item?.category}-${index}`}
                        className="flex items-start gap-2 border border-gray-200 rounded-lg p-2 cursor-pointer hover:bg-gray-50 mb-2"
                        onClick={() => {
                          const isMine = item?.user?.id === Number(Cookies.get('userId'));

                          // Track priority item click
                          mixpanel.track('Priority Item Clicked', {
                            user_id: userID,
                            priority_type: item.category,
                            target_user_id: item?.user?.id,
                            target_user_name: item?.user?.name,
                            is_mine: isMine,
                            count: item?.count,
                            project_id: filters.projectID,
                            timestamp: new Date().toISOString()
                          });

                          if (isMine) {
                            if (item.category === 'invoiceGenerated') {
                              toast('invoiceGenerated');
                              return;
                            }
                            let filterType = 'Default';
                            if (item.category === 'payment') {
                              filterType = 'Payment';
                            } else if (item.category === 'possession') {
                              filterType = 'Possession Date';
                            } else if (item.category === 'closeddeal') {
                              filterType = 'ClosedDeal Details';
                            } else if (item.category === 'underConsideration') {
                              filterType = 'UnderConsideration Details';
                            } else if (item.category === 'reviewMeeting') {
                              navigate('/builder/sales/reports', {
                                state: {
                                  mode: 'viewOnly',
                                  projectID: filters.projectID,
                                  month: filters.month
                                }
                              });
                              return; // Exit early since we've navigated
                            } else if (item.category === 'reportReview') {
                              // Navigate to Reports with dashboard mode for Report Review
                              // Get the follow-up ID from the items array
                              const followUpId = item?.items?.[0]?.id || null;
                              navigate('/builder/sales/reports', {
                                state: {
                                  mode: 'dashboard',
                                  projectID: filters.projectID,
                                  month: filters.month,
                                  priorityId: followUpId,
                                  isFollowUpPriority: true // Flag to indicate this is a follow-up from SalesDashboard
                                }
                              });
                              return; // Exit early since we've navigated
                            }

                            navigate('/builder/priority/leads', {
                              state: {
                                salesID: item?.user?.id,
                                salesName: item?.user?.name,
                                filterType: filterType,
                                followUpDate: item.date === 'No Date' ? null : item.date,
                                projectID: filters.projectID
                              }
                            });
                          } else if (item.category === 'escalated') {
                            navigate('/builder/priority/leads', {
                              state: {
                                salesID: item?.user?.id,
                                salesName: item?.user?.name,
                                filterType: 'Escalated',
                                projectID: filters.projectID
                              }
                            });
                          } else if (item.category === 'absent') {
                            navigate('/builder/priority/leads', {
                              state: {
                                salesID: item?.user?.id,
                                salesName: item?.user?.name,
                                filterType: 'Absent',
                                projectID: filters.projectID
                              }
                            });
                          }
                        }}>
                        <span className="text-sm flex items-center gap-2">
                          <AvatarCircle name={item?.user?.name} />
                          <strong>{item?.user?.name}</strong> | {label} | {item?.count}{' '}
                          {item?.count === 1 ? 'follow-up' : 'follow-ups'}
                        </span>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-8">
                    <div className="text-gray-400 text-4xl mb-2">📋</div>
                    <p className="text-sm text-gray-500">No tasks available</p>
                  </div>
                )}
              </div>

              {/* Pending Approvals Section */}
              {designation !== 'Low' && (
                <div className="border border-gray-300 rounded-lg p-4">
                  <h3 className="text-md font-semibold mb-4">Pending Approvals</h3>
                  {filteredAvailabilityData?.length > 0 ? (
                    filteredAvailabilityData.map(([date, items]) => (
                      <div key={date} className="mb-4">
                        <p className="font-semibold text-sm text-gray-600 mb-2">{date}</p>
                        <div className="space-y-2">
                          {items.map((item) => (
                            <div
                              className="flex items-start gap-2 border border-gray-200 rounded-lg p-2 cursor-pointer hover:bg-gray-50"
                              key={item.id}
                              onClick={() => {
                                // Track approval item click
                                mixpanel.track('Approval Item Clicked', {
                                  user_id: userID,
                                  activity_id: item.id,
                                  target_user_id: item?.user?.id,
                                  target_user_name: item?.user?.name,
                                  date: formatDate(item.date),
                                  project_id: filters.projectID,
                                  timestamp: new Date().toISOString()
                                });

                                setShowConfirmationModal(true);
                                setSelectedActivity(item.id);
                              }}>
                              <span className="text-sm flex items-center gap-2">
                                <AvatarCircle name={item?.user?.name} />
                                <strong>{item?.user?.name}</strong> | Leave | Requesting For Leave
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8">
                      <div className="text-gray-400 text-4xl mb-2">📋</div>
                      <p className="text-sm text-gray-500">No tasks available</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );

  // Wrap in Layout only if not skipped
  if (skipLayout) {
    return content;
  }

  return <Layout>{content}</Layout>;
};

export default SalesDashboard;
